import { DateTime } from 'luxon';
import { RRule, RRuleSet } from 'rrule';
import { emptyEventMetadata } from '@/features/eventMetadata/lib/emptyMetadata.lib';
import { createDecoratedMagicEvent } from '@/features/events/lib/createDecoratedMagicEvent';
import type { DecoratedMagicEvent, MagicEvent } from '@/features/magic/types';
import { EventItemKind } from '@/types/gql.generated';
import type { GetQuickEntriesPageQuery } from './useGetMagicSharePage.generated';

type QuickEntry =
  GetQuickEntriesPageQuery['getQuickEntriesPage']['quickEntries'][number];

const toISODate = (str: string): string => {
  return str.replace(/T.*$/, '');
};

export const convertQuickEntryToMagicEvent = (
  quickEntry: QuickEntry
): DecoratedMagicEvent => {
  const { startDate, endDate, isOnDay, rule } = quickEntry.recurrences[0];

  let lastEndDate = toISODate(endDate);
  let lastEndDateTime = endDate;
  if (rule) {
    const options = RRule.parseString(rule);
    const recurrenceStart = DateTime.fromISO(startDate);
    const recurrenceEnd = DateTime.fromISO(endDate);
    const duration = recurrenceEnd.diff(recurrenceStart, 'minutes').minutes;

    options.dtstart = recurrenceStart.toJSDate();
    if (!options.until && !options.count) {
      options.until = DateTime.fromISO('2037-08-17T08:55:00.000Z').toJSDate();
    }
    const rruleSet = new RRuleSet();
    rruleSet.rrule(new RRule(options));
    const instances = rruleSet.all();

    // the last instance refers to the last "start time" of the recurring series
    // so we have to add back on the duration of the event in order to get the end date/time
    const lastInstance = instances[instances.length - 1];
    const lastInstanceDate = DateTime.fromJSDate(lastInstance).plus({
      minutes: duration,
    });
    lastEndDate = lastInstanceDate.toISODate();
    lastEndDateTime = lastInstanceDate.toISO();
  }

  const magicEvent: MagicEvent = {
    ...quickEntry,
    title: quickEntry.title || '',
    rrule: rule,
    isAllDay: isOnDay,
    start: {
      date: isOnDay ? toISODate(startDate) : null,
      dateTime: isOnDay ? null : startDate,
      timeZone: isOnDay ? null : quickEntry.timeZone,
    },
    end: {
      date: isOnDay ? toISODate(endDate) : null,
      dateTime: isOnDay ? null : endDate,
      timeZone: isOnDay ? null : quickEntry.timeZone,
    },
    lastEndDate,
    lastEndDateTime,
    kind: EventItemKind.Event,
    version: 1,
    exports: [],
    // A shared quick-create page has no experience behind it, so there are no
    // per-experience overrides to carry.
    metadata: emptyEventMetadata(),
  };

  return createDecoratedMagicEvent(magicEvent);
};
